import { useToast } from "@apex/component-library";
import axios, { AxiosError } from "axios";
import { useQuery } from "react-query";

import { DEFAULT_ERROR_TOAST } from "../utils/constants";
import { getApiFormattedDate } from "../utils/dateFormatter";
import { getAppConfigSetting } from "../utils/getAppConfigSetting";
import { GET_REQUIREMENTS_API } from "./constants";
import queryKeys from "./queryKeys";

export interface RequirementsDetails {
  type: string;
  account: string;
  processDate: string;
  accountType: string;
  orderID: string | null;
  tradeNumber: string;
  tradeDate: string;
  executionTime: string;
  symbol: string;
  shareQuantity: number;
  netAmount: number;
  sharePrice: number;
  currencyCode: string;
  requirement: number;
  requirementType: string;
  underlyingSymbolClass: string;
  unavailableBalance: number;
  buySellCode: string;
  preMeet: number | null;
  highWaterMark: number;
  isOpeningPosition: number | null;
  buySellCodeWithOpeningPosition: string;
  settlementDate: string;
  runningOpenPosition: number;
}

export interface RequirementsSummaries {
  account: string;
  processDate: string;
  reqClass: string;
  totalRequirement: number;
  type: string;
}

export interface RequirementsResponse {
  account: string;
  requirementsDetails: RequirementsDetails[];
  requirementsSummaries: RequirementsSummaries[];
}

const useGetRequirements = (
  accountId: string,
  fromProcessDate: string | Date,
  toProcessDate: string | Date,
  requirementType?: string
) => {
  let marginsApiURL = `${getAppConfigSetting("apiUrl")}${GET_REQUIREMENTS_API(
    accountId
  )}?fromDate=${getApiFormattedDate(
    fromProcessDate
  )}&toDate=${getApiFormattedDate(toProcessDate)}`;

  if (requirementType) {
    marginsApiURL = `${marginsApiURL}&type=${requirementType}`;
  }

  const toast = useToast();

  const fetchCustomerSummary = async () => {
    const { data } = await axios.get(marginsApiURL, {
      withCredentials: true,
    });
    return data as RequirementsResponse;
  };

  return useQuery<RequirementsResponse, AxiosError>(
    [
      queryKeys.getRequirements,
      accountId,
      fromProcessDate,
      toProcessDate,
      requirementType,
    ],
    fetchCustomerSummary,
    {
      enabled: false,
      onError: ({ response }: any) => {
        toast({ ...DEFAULT_ERROR_TOAST, description: response.data.message });
      },
    }
  );
};

export default useGetRequirements;
